/**
 * @fileoverview Defines a tool that allows asking questions about the codebase using a language model. It takes a question and optional URL as input and returns an answer generated by the LLM. The tool leverages the `askQuestion` function to query the codebase.
 */

import { askQuestion } from '../ask'
import { ToolDefinition } from '@iterator/shared-node/ai/llm_types'
import { ClaudeSonnetModel } from '@src/ai/models'
import z from 'zod'

export const AskArgs = z.object({
  question: z.string(),
  url: z.string().optional(),
})

export type AskCall = {
  name: 'ask'
  args: z.infer<typeof AskArgs>
}

export async function ask(question: string, url?: string) {
  return await askQuestion(
    process.cwd(),
    question,
    ClaudeSonnetModel,
    'documentation',
    undefined,
    url,
  )
}

export const askTool: ToolDefinition = {
  name: 'ask',
  function: {
    name: 'ask',
    description: 'Ask questions about the codebase.',
    parameters: {
      type: 'object',
      properties: {
        question: {
          type: 'string',
          description: 'The question to ask about the codebase',
        },
        url: {
          type: 'string',
          description: 'Optional URL of markdown content to include in context',
        },
      },
      required: ['question'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { question, url } = AskArgs.parse(args)
    return await ask(question, url)
  },
}
